// lingue.ts — le lingue di Laughdown, condivise tra app e API.
// Codici BCP-47. L'elenco attivo in produzione è l'impostazione app.lingue_attive;
// questo è l'insieme delle lingue per cui esistono traduzioni.

export const LINGUE_SUPPORTATE = [
  "ar", "be", "bg", "bn", "cs", "da", "de", "el", "en", "es-ES", "fi", "fil", "fr", "hi", "hr", "hu",
  "id", "it", "ja", "ko", "nl", "nb", "pl", "pt-PT", "ro", "ru", "sv", "th", "tr", "uk", "vi", "zh-Hans",
] as const;

export type Lingua = (typeof LINGUE_SUPPORTATE)[number];

export const LINGUA_RIPIEGO: Lingua = "en";

/** Lingue scritte da destra a sinistra. */
export const LINGUE_RTL: readonly Lingua[] = ["ar"];

/**
 * Alias: come i browser e i telefoni indicano una lingua → la nostra.
 * Chiavi in minuscolo; si confronta prima il tag intero, poi la sola lingua.
 */
const ALIAS: Record<string, Lingua> = {
  es: "es-ES",
  pt: "pt-PT",
  "pt-br": "pt-PT", // finché non esiste pt-BR si usa il portoghese del Portogallo
  zh: "zh-Hans",
  "zh-cn": "zh-Hans",
  "zh-sg": "zh-Hans",
  "zh-hans-cn": "zh-Hans",
  no: "nb",
  nn: "nb",
  "nb-no": "nb",
  tl: "fil",
  in: "id", // vecchio codice dell'indonesiano usato da alcuni Android
};

const PER_MINUSCOLO = new Map<string, Lingua>(LINGUE_SUPPORTATE.map((l) => [l.toLowerCase(), l]));

/** Riconduce un tag di lingua qualsiasi a una lingua supportata, oppure null. */
export function normalizzaLingua(tag: string | null | undefined): Lingua | null {
  if (!tag) return null;
  const t = tag.trim().replace(/_/g, "-").toLowerCase();
  if (!t) return null;
  const diretta = PER_MINUSCOLO.get(t) ?? ALIAS[t];
  if (diretta) return diretta;
  const base = t.split("-")[0] ?? "";
  return PER_MINUSCOLO.get(base) ?? ALIAS[base] ?? null;
}

/**
 * Sceglie la lingua migliore da un elenco di preferenze (es. navigator.languages
 * o l'intestazione Accept-Language già divisa), limitandosi alle lingue attive.
 */
export function scegliLingua(
  preferenze: readonly string[],
  attive: readonly string[] = LINGUE_SUPPORTATE,
  predefinita: string = LINGUA_RIPIEGO,
): Lingua {
  const insiemeAttive = new Set(attive);
  for (const p of preferenze) {
    const l = normalizzaLingua(p);
    if (l && insiemeAttive.has(l)) return l;
  }
  const pred = normalizzaLingua(predefinita);
  if (pred && insiemeAttive.has(pred)) return pred;
  return LINGUA_RIPIEGO;
}

/** Divide un'intestazione Accept-Language in tag ordinati per peso (q). */
export function leggiAcceptLanguage(intestazione: string | null | undefined): string[] {
  if (!intestazione) return [];
  return intestazione
    .split(",")
    .map((parte, indice) => {
      const [tag, ...parametri] = parte.trim().split(";");
      const q = parametri.map((p) => p.trim()).find((p) => p.startsWith("q="));
      const peso = q ? Number(q.slice(2)) : 1;
      return { tag: (tag ?? "").trim(), peso: Number.isFinite(peso) ? peso : 0, indice };
    })
    .filter((x) => x.tag && x.tag !== "*" && x.peso > 0)
    .sort((a, b) => b.peso - a.peso || a.indice - b.indice)
    .map((x) => x.tag);
}

export function eRtl(lingua: string): boolean {
  return (LINGUE_RTL as readonly string[]).includes(lingua);
}
